import { AbstractControl } from '@angular/forms';
import {
  ActionTypeEnum,
  AreaTypeEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';

/**
 * Aktionsarten je Sparte, bei denen die Zählernummer der durchzuführenden Aktion den vorhandenen
 * Zähler bezeichnet. Bei diesen Aktionen wird sie in die Zählerwechselinformationen
 * („Alter Zähler" → `existingDevice.deviceNumber`) übernommen (Ticket 5471).
 *
 * Strom „Änderung mitteilen" gehört bewusst nicht dazu. Strom „kurzfristige Wiederinbetriebnahme"
 * ist nicht mehr auswählbar, kommt aber in bestehenden Anträgen noch vor und zeigt dort das Feld.
 */
const actionTypesWithExistingMeterNumber: Partial<Record<AreaTypeEnum, ActionTypeEnum[]>> = {
  [AreaTypeEnum.POWER]: [
    ActionTypeEnum.decommissioning,
    ActionTypeEnum.change,
    ActionTypeEnum.sealing,
    ActionTypeEnum.recommissioning,
    ActionTypeEnum.temporaryRecommissioning,
    ActionTypeEnum.inspection
  ],
  [AreaTypeEnum.GAS]: [
    ActionTypeEnum.decommissioning,
    ActionTypeEnum.changeCommunicate,
    ActionTypeEnum.recommissioning,
    ActionTypeEnum.temporaryRecommissioning,
    ActionTypeEnum.inspection
  ]
};

/** Gilt die Übernahme der Zählernummer für diese Sparte und Aktionsart? */
export function isMeterNumberTransferActionType(area: AreaTypeEnum, actionType: ActionTypeEnum): boolean {
  return actionTypesWithExistingMeterNumber[area]?.includes(actionType) ?? false;
}

/**
 * Schreibt die Zählernummer der Aktion in „Alter Zähler" → Zählernummer. Gedacht für die
 * Eingabe des Nutzers im Feld „Zählernummer": der Wert wird jedes Mal übernommen, das Zielfeld
 * bleibt danach frei bearbeitbar. Programmatische Änderungen (Laden, Wechsel der Aktionsart)
 * rufen diese Funktion nicht auf.
 */
export function transferMeterNumberToExistingDevice(action: AbstractControl, area: AreaTypeEnum): void {
  if (!isMeterNumberTransferActionType(area, action.get('actionType')?.value)) {
    return;
  }
  const deviceNumber = action.get('existingDevice.deviceNumber');
  if (!deviceNumber) {
    return;
  }
  deviceNumber.setValue(action.get('meterNumber')?.value ?? null);
  deviceNumber.markAsDirty();
}

/**
 * Gegenrichtung zu {@link transferMeterNumberToExistingDevice}: schreibt die Eingabe unter
 * „Alter Zähler" → Zählernummer in das Feld „Zählernummer" der Aktion, bei denselben Aktionsarten.
 */
export function transferExistingDeviceNumberToMeterNumber(action: AbstractControl, area: AreaTypeEnum): void {
  if (!isMeterNumberTransferActionType(area, action.get('actionType')?.value)) {
    return;
  }
  const meterNumber = action.get('meterNumber');
  if (!meterNumber) {
    return;
  }
  meterNumber.setValue(action.get('existingDevice.deviceNumber')?.value ?? null);
  meterNumber.markAsDirty();
}

/**
 * Belegt „Alter Zähler" → Zählernummer mit der Zählernummer der Aktion vor, solange das Zielfeld
 * leer ist. Einsatz dort, wo die Zählernummer nicht per Eingabe übernommen werden konnte: im
 * Abschluss-Popup (zeigt die Zählernummer der Aktion nicht) und in der Sachbearbeiter-Sicht, wenn
 * der Bereich „Zählerwechselinformationen" für Installateure ausgeblendet ist.
 */
export function prefillExistingDeviceNumber(action: AbstractControl, area: AreaTypeEnum): void {
  if (!isMeterNumberTransferActionType(area, action.get('actionType')?.value)) {
    return;
  }
  const deviceNumber = action.get('existingDevice.deviceNumber');
  const meterNumber = action.get('meterNumber')?.value;
  if (!deviceNumber || deviceNumber.value || !meterNumber?.toString().trim()) {
    return;
  }
  deviceNumber.setValue(meterNumber);
}
